import { Building2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { SettingsPageLayout } from "components/settings/settings-page-layout";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  fetchOrganisation,
  fetchOrganisationMemberships,
} from "data/queries-common";
import { resolveIdentityId } from "utils/identity";

import { ContactDetailsCard } from "./contact-details-card";
import { OrganisationProfileCard } from "./organisation-profile-card";
import { TransferOwnershipDialog } from "./transfer-ownership-dialog";

export const GeneralSettingsPage = () => {
  const [isTransferOpen, setIsTransferOpen] = useState(false);

  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);

  const organisationQuery = useQuery({
    queryKey: [QueryKeys.organisation, identityId],
    queryFn: () => fetchOrganisation(),
  });

  const organisationInvitationsQuery = useQuery({
    queryKey: [QueryKeys.organisationInvitations, identityId],
    queryFn: () => fetchOrganisationMemberships(identityId),
  });

  const { isPermissionsLoading, isPermissionsSuccess } = usePermissions();

  const isLoading =
    organisationQuery.isPending ||
    organisationInvitationsQuery.isPending ||
    isPermissionsLoading;
  const isSuccess =
    organisationQuery.isSuccess &&
    organisationInvitationsQuery.isSuccess &&
    isPermissionsSuccess;

  return (
    <SettingsPageLayout
      pageTitle={{
        icon: Building2,
        title: "Organisation General Settings",
        description:
          "Manage your organisation profile and contact details, and transfer ownership to another member.",
      }}
    >
      <div className="mx-auto max-w-5xl space-y-4">
        <ReactQueryErrorWrapper
          queries={[organisationQuery, organisationInvitationsQuery]}
        />

        {isLoading && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Organisation Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <Skeleton className="h-80 w-full" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Contact Details</CardTitle>
              </CardHeader>
              <CardContent>
                <Skeleton className="h-56 w-full" />
              </CardContent>
            </Card>
          </>
        )}

        {isSuccess && (
          <>
            <OrganisationProfileCard organisation={organisationQuery.data} />

            <PermissionsWrapper
              keys={[PermissionsKeys.membersChangeOwner]}
              messages={{ [PermissionsReasons.ROLE_DENY]: <></> }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Transfer Ownership</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Permanently transfer ownership of this organisation to
                    another member. This action cannot be undone.
                  </p>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button type="button" onClick={() => setIsTransferOpen(true)}>
                    Transfer Ownership
                  </Button>
                </CardFooter>
              </Card>
            </PermissionsWrapper>

            <ContactDetailsCard organisation={organisationQuery.data} />

            <TransferOwnershipDialog
              open={isTransferOpen}
              onOpenChange={setIsTransferOpen}
              organisationId={organisationQuery.data.id ?? ""}
              organisationName={organisationQuery.data.name ?? ""}
              members={organisationInvitationsQuery.data ?? []}
            />
          </>
        )}
      </div>
    </SettingsPageLayout>
  );
};
